import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateDynamic, translateDynamicMany } from '@/i18n/autoTranslate';

/**
 * useDynamicTranslate — translate a single admin/dynamic string to the
 * active UI language. Returns the original text until the API responds.
 *
 *   const title = useDynamicTranslate(settings.title);
 */
export function useDynamicTranslate(text) {
  const { i18n } = useTranslation();
  const [value, setValue] = useState(text);
  useEffect(() => {
    let cancelled = false;
    if (!text) { setValue(text); return; }
    if (i18n.language === 'hr') { setValue(text); return; }
    translateDynamic(text, i18n.language).then(v => {
      if (!cancelled) setValue(v);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [text, i18n.language]);
  return value;
}

/**
 * useDynamicTranslateMany — translate a list of strings at once.
 * Returns a map `{ original: translated }`.
 */
export function useDynamicTranslateMany(texts) {
  const { i18n } = useTranslation();
  const [map, setMap] = useState({});
  const keyList = (texts || []).filter(Boolean);
  const sig = keyList.join('||') + '::' + i18n.language;
  useEffect(() => {
    let cancelled = false;
    if (!keyList.length) { setMap({}); return; }
    if (i18n.language === 'hr') {
      setMap(Object.fromEntries(keyList.map(t => [t, t])));
      return;
    }
    translateDynamicMany(keyList, i18n.language).then(m => {
      if (!cancelled) setMap(m);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return map;
}
